import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'

import { setModal } from 'slices/modalSlice'
import { toastErrorLog } from '../../../helpers/toastErrorLog'
import {
  goBackToCategoria,
  resetCategorias,
  setCategoria,
  setEstado,
  setFiltrosCaracteristicas,
  setFiltrosSeleccionados,
  setPrecio,
  setPublicacionesFiltradas,
  setTipoVendedor
} from '../../../slices/filterSlice'
import * as categoriasService from '../../servicios/categoriasService'
import { buscarMetadato as buscarMetadatoPublicacion } from '../../servicios/publicacionesService'
import { buscarTodosPorIdCategoria } from '../../servicios/subcategoriaService'
import { Buscador } from '../buscador/Buscador'
import { FechaSelect } from '../fecha/FechaSelect'
import { GenericModal } from '../modal/GenericModal'
import styles from './Filtros.module.css'
import { RadioInput } from './RadioInput'

const ORDEN_POR_MENOR_PRECIO = 2
const ORDEN_POR_MAYOR_PRECIO = 3
const MAX_OPCIONES_VISIBLES = 5

export const Filtros = ({
  publicaciones,
  filtrosFijos,
  setVendedor,
  vendedor,
  buscador,
  mostrarFiltroPrecio = true,
  provinciaSeleccionada = '',
  localidadSeleccionada = '',
  setProvinciaSeleccionada,
  setLocalidadSeleccionada,
  fechaSeleccionada,
  limpiarFiltros,
  mostrarLimpiarFiltros
}) => {
  const dispatch = useDispatch()
  const [categorias, setCategorias] = useState([])
  const [caracteristicasActuales, setCaracteristicasActuales] = useState([])
  const [mapEtiquetaIds, setMapEtiquetaIds] = useState({})
  const [busqueda, setBusqueda] = useState('')
  const {
    categoriaSeleccionada,
    categoriasPath,
    tipoVendedor,
    estado,
    filtrosCaracteristicas,
    orden,
    precioDesde,
    precioHasta
  } = useSelector((state) => state.filters)
  const filtrosSeleccionados = useSelector(
    (state) => state.filters.filtrosSeleccionados,
    shallowEqual
  )
  const modalMetadata = useSelector((state) => state.genericModal.metadata)
  const buscarFiltros = async (publicaciones, { esFinal, esRaiz }) => {
    //snapshot de la categoria seleccionada
    const categoriaInicial = categoriaSeleccionada

    // Obtener IDs de publicaciones para pedir metadatos
    const ids = publicaciones.map((p) => p.idPublicacion).join(',')
    if (!publicaciones?.length) {
      return
    }

    //Trae todos los atributos asociados a las publicaciones visibles.
    const { data: dataPub } = await buscarMetadatoPublicacion(ids)
    if (categoriaSeleccionada !== categoriaInicial) {
      return
    }
    setCaracteristicasActuales(dataPub)

    let dataFiltrada = []

    //Filtrado de atributos segun nivel de categoria
    if (esFinal) {
      dataFiltrada = dataPub
    } else if (!esRaiz) {
      const { data: dataCat } =
        await categoriasService.buscarAtributos(categoriaSeleccionada)
      if (categoriaSeleccionada !== categoriaInicial) {
        return
      }

      const importantesArr = dataCat.filter((a) => Number(a.catalogRequired) === 1)

      const idToLabel = new Map(
        importantesArr.map((a) => [a.idMetaDato, a.Etiqueta ?? ''])
      )

      //filtramos los atributos totales de las publicaciones para traer los importantes
      dataFiltrada = dataPub
        .filter((car) => idToLabel.has(car.idMetaDato))
        .map((car) => ({
          ...car,
          etiqueta: idToLabel.get(car.idMetaDato) || car.etiqueta
        }))
    } else {
      dataFiltrada = []
    }

    const filtros = {}
    const filtrosArray = {}
    const filtrosSelected = {}
    const etiquetaIds = {}

    dataFiltrada.forEach((car) => {
      const key = car.etiqueta
      let valor = car.valor

      if (car.idMetadatoTipo === 'string' && (!valor || valor.trim() === '')) {
        return
      }

      if (car.idMetadatoTipo === 'boolean') {
        valor =
          valor.toUpperCase() === 'SI' || valor.toUpperCase() === 'YES' ? 'SI' : 'NO'
      }

      if (!filtros[key]) {
        filtros[key] = new Set()
        etiquetaIds[key] = new Set()
      }
      filtros[key].add(String(valor).toLowerCase())
      etiquetaIds[key].add(car.idMetaDato)
    })

    // orden de filtros y valores
    const claves = Object.keys(filtros)

    claves.sort().forEach((key) => {
      const arr = Array.from(filtros[key])
        .filter((v) => v !== '')
        .sort((a, b) => a.localeCompare(b))
      if (arr.length > 0) {
        filtrosArray[key] = arr
        filtrosSelected[key] = filtrosSelected[key] ?? ''
      }
    })

    const etiquetaIdsObj = Object.fromEntries(
      Object.entries(etiquetaIds).map(([k, v]) => [k, Array.from(v)])
    )
    setMapEtiquetaIds(etiquetaIdsObj)

    dispatch(setFiltrosCaracteristicas(filtrosArray))
    if (Object.keys(filtrosSeleccionados).length === 0) {
      dispatch(setFiltrosSeleccionados(filtrosSelected))
    }
  }

  const buscarCategorias = async (publicaciones) => {
    // Si no hay categoría seleccionada (raíz), obtener las categorías raíz
    // Si hay categoría, obtener sus subcategorías
    try {
      const { data } = await buscarTodosPorIdCategoria(categoriaSeleccionada || 0, false)
      const esFinal = data.length === 0
      const esRaiz = !categoriaSeleccionada

      if (!esFinal) {
        const categorias = data

          .map(({ idSubCategoria, descripcion, tieneSubCategoria }) => {
            if (publicaciones.some((p) => p.categoriaRoot.includes(idSubCategoria))) {
              return {
                value: idSubCategoria,
                label: tieneSubCategoria
                  ? descripcion
                  : `${descripcion} (${
                      publicaciones.filter((p) => p.idCategoria === +idSubCategoria)
                        .length
                    })`,
                tieneSubCategoria
              }
            }
            return null
          })
          .filter(Boolean)

        setCategorias(categorias)
        buscarFiltros(publicaciones, { esFinal, esRaiz })
      } else {
        setCategorias([])
        buscarFiltros(publicaciones, { esFinal, esRaiz })
      }
    } catch (error) {
      const mensaje = 'Error al cargar las categorias'
      toastErrorLog(error, mensaje, 'filtros')
      setCategorias([])
      buscarFiltros(publicaciones, { esFinal: true, esRaiz: !categoriaSeleccionada })
    }
  }

  useEffect(() => {
    if (publicaciones && publicaciones.length > 0) {
      const todasPublicaciones = publicaciones.filter((p) => {
        if (!p.categoriaRoot) {
          return false
        }

        let categorias
        try {
          categorias = JSON.parse(p.categoriaRoot).map((c) => c.id)
        } catch {
          return false
        }

        const matchCategoria =
          !categoriaSeleccionada || categorias.includes(Number(categoriaSeleccionada))
        const matchVendedor = !tipoVendedor || Number(p.idPerfil) === tipoVendedor
        const matchEstado =
          !estado || (estado === 'nuevo' ? p.nuevo === true : p.nuevo === false)

        const precioSinIva = Number(p.precio)
        const alicuota = Number(p.alicuota) || 0
        const precioConIva = precioSinIva * (1 + alicuota / 100)
        const matchPrecioDesde = !precioDesde || precioConIva >= Number(precioDesde)
        const matchPrecioHasta = !precioHasta || precioConIva <= Number(precioHasta)

        const filtrosArray = Object.entries(filtrosSeleccionados).filter(
          ([, valor]) => valor
        )

        const matchCaracteristicas = filtrosArray.every(([etiqueta, valor]) => {
          if (!valor || caracteristicasActuales.length === 0) {
            return true
          }

          const valorNormalizado = valor?.toString().trim().toLowerCase()
          const etiquetaNormalizada = etiqueta?.toString().trim().toLowerCase()
          const idsAsociados = mapEtiquetaIds[etiqueta] || []

          const existe = caracteristicasActuales.some((c) => {
            const etiquetaCar = c.etiqueta?.toString().trim().toLowerCase()
            const valorCar = (c.valor ?? '').toString().trim().toLowerCase()
            const matchId = idsAsociados.includes(c.idMetaDato)
            const matchEtiqueta = etiquetaCar === etiquetaNormalizada
            const matchValor = valorCar === valorNormalizado
            return (
              c.idPublicacion === p.idPublicacion &&
              matchValor &&
              (matchId || matchEtiqueta)
            )
          })

          return existe
        })

        // Filtrar por provincia y localidad del vendedor
        // Buscar en TODOS los domicilios válidos del vendedor
        const domicilios = p.domiciliosVendedor || []
        let matchProvincia = true
        let matchLocalidad = true

        if (provinciaSeleccionada || localidadSeleccionada) {
          // Si hay filtros de ubicación, verificar que al menos un domicilio coincida
          const tieneCoincidencia = domicilios.some((dom) => {
            const provinciaMatch =
              !provinciaSeleccionada ||
              dom.provincia?.toString().trim().toLowerCase() === provinciaSeleccionada
            const localidadMatch =
              !localidadSeleccionada ||
              (dom.localidad || dom.ciudad)?.toString().trim().toLowerCase() ===
                localidadSeleccionada
            return provinciaMatch && localidadMatch
          })
          matchProvincia = tieneCoincidencia
          matchLocalidad = tieneCoincidencia
        }

        return (
          matchCategoria &&
          matchVendedor &&
          matchEstado &&
          matchPrecioDesde &&
          matchPrecioHasta &&
          matchCaracteristicas &&
          matchProvincia &&
          matchLocalidad
        )
      })

      let publicacionesOrdenadas
      if (orden === ORDEN_POR_MENOR_PRECIO) {
        publicacionesOrdenadas = [...todasPublicaciones].sort(
          (a, b) => a.precio - b.precio
        )
      } else if (orden === ORDEN_POR_MAYOR_PRECIO) {
        publicacionesOrdenadas = [...todasPublicaciones].sort(
          (a, b) => b.precio - a.precio
        )
      } else {
        publicacionesOrdenadas = todasPublicaciones
      }
      dispatch(setPublicacionesFiltradas(publicacionesOrdenadas))
      buscarCategorias(publicacionesOrdenadas)
    }
  }, [
    publicaciones,
    categoriaSeleccionada,
    tipoVendedor,
    estado,
    orden,
    precioDesde,
    precioHasta,
    filtrosSeleccionados,
    provinciaSeleccionada,
    localidadSeleccionada
  ])
  const onChangeCategoria = (id, label) => {
    setBusqueda('')
    setCaracteristicasActuales([])
    dispatch(setCategoria({ id, label }))
    dispatch(setFiltrosSeleccionados({}))
    dispatch(setFiltrosCaracteristicas({}))
    dispatch(setPrecio({ desde: '', hasta: '' }))
    setCategorias([])
  }

  const categoriaAnterior = (id) => {
    dispatch(goBackToCategoria(id))
    setCategorias([])
    setCaracteristicasActuales([])
    dispatch(setFiltrosSeleccionados({}))
    dispatch(setFiltrosCaracteristicas({}))
    dispatch(setPrecio({ desde: '', hasta: '' }))
    setMapEtiquetaIds({})
  }

  const onResetCategorias = () => {
    dispatch(resetCategorias())
    setCategorias([])
    setCaracteristicasActuales([])
    dispatch(setFiltrosSeleccionados({}))
    dispatch(setFiltrosCaracteristicas({}))
    dispatch(setPrecio({ desde: '', hasta: '' }))
  }

  const limpiarTodosFiltros = () => {
    onResetCategorias()
    dispatch(setTipoVendedor(''))
    dispatch(setEstado(''))
    setMapEtiquetaIds({})
    setBusqueda('')
    if (setVendedor) {
      setVendedor({ id: 0, label: '' })
    }
    if (setProvinciaSeleccionada) {
      setProvinciaSeleccionada('')
    }
    if (setLocalidadSeleccionada) {
      setLocalidadSeleccionada('')
    }
    if (limpiarFiltros) {
      limpiarFiltros()
    }
  }

  const verTodas = (opciones, tipo = 'categoria', etiqueta = null, onChange = null) => {
    dispatch(
      setModal({
        size: '',
        title:
          tipo === 'categoria'
            ? 'Selecciona una categoría'
            : `Filtrar por ${etiqueta || tipo}`,
        type: '',
        show: true,
        metadata: { tipo, etiqueta, onChange }
      })
    )
    dispatch(setModal({ items: opciones }))
  }

  const ordenarOpciones = (opciones, value) => {
    if (!value) {
      return opciones
    }

    return [...opciones].sort((a, b) => {
      if (a.valor === value) {
        return -1
      }
      if (b.valor === value) {
        return 1
      }
      return 0
    })
  }

  const handlePrecioChange = (value, tipo) => {
    if (value === '' || Number(value) >= 0) {
      if (tipo === 'desde') {
        dispatch(setPrecio({ desde: value, hasta: precioHasta }))
      } else {
        dispatch(setPrecio({ desde: precioDesde, hasta: value }))
      }
    }
  }

  const handleKeyDownPrecio = (e) => {
    if (e.key === '-' || e.key === 'e' || e.key === 'E') {
      e.preventDefault()
    }
  }

  return (
    <div className={`${styles.filtros}`}>
      <GenericModal
        onSelect={(item) => {
          const tipo = modalMetadata?.tipo || 'categoria'

          if (tipo === 'categoria') {
            onChangeCategoria(item.value, item.label)
          } else if (tipo === 'caracteristica') {
            const nuevosFiltros = {
              ...filtrosSeleccionados,
              [modalMetadata.etiqueta]: item.value || item.label
            }
            dispatch(setFiltrosSeleccionados(nuevosFiltros))
          } else if (tipo === 'filtroFijo') {
            if (modalMetadata.onChange) {
              modalMetadata.onChange(item.valor)
            }
          }
        }}
      />
      {buscador && (
        <Buscador
          onChange={buscador.onChange}
          placeholder={buscador.placeholder}
          busqueda={busqueda}
          setBusqueda={setBusqueda}
        />
      )}
      {(categoriasPath.length > 0 ||
        (vendedor && vendedor.id !== 0) ||
        Object.values(filtrosSeleccionados).some((v) => v) ||
        precioDesde ||
        precioHasta ||
        mostrarLimpiarFiltros) && (
        <div className={styles.limpiarFiltrosContainer}>
          <button className={styles.btnLimpiarFiltros} onClick={limpiarTodosFiltros}>
            Limpiar filtros
          </button>
        </div>
      )}
      {(categoriasPath.length > 0 || (vendedor && vendedor.id !== 0)) && (
        <div className={styles.filtrosActivos}>
          {categoriasPath.length > 0 && (
            <div className={styles.pathContainer}>
              {categoriasPath.map((cat, index) => (
                <div key={cat.id}>
                  <span
                    className={styles.categoriaPath}
                    onClick={() => categoriaAnterior(cat.id)}
                  >
                    {cat.label}
                  </span>
                  {index < categoriasPath.length - 1 && ' > '}
                </div>
              ))}
              <span className={styles.clearPath} onClick={onResetCategorias}>
                <i className="bi bi-x"></i>
              </span>
            </div>
          )}
          {vendedor && vendedor.id !== 0 && (
            <div className={styles.pathContainer}>
              <span className={styles.categoriaPath}>{vendedor.label}</span>
              <span
                className={styles.clearPath}
                onClick={() => setVendedor({ id: 0, label: '' })}
              >
                <i className="bi bi-x"></i>
              </span>
            </div>
          )}
        </div>
      )}
      {categorias.length > 0 && (
        <div className={styles.filtroItem}>
          <h3>Categoria:</h3>
          {categorias.slice(0, MAX_OPCIONES_VISIBLES).map((cat) => (
            <span
              key={cat.value}
              className={styles.crumb}
              onClick={() => onChangeCategoria(cat.value, cat.label)}
            >
              {cat.label}
            </span>
          ))}
          {categorias.length > MAX_OPCIONES_VISIBLES && (
            <span
              className={styles.verMas}
              onClick={() => verTodas(categorias, 'categoria')}
            >
              Ver Todas
            </span>
          )}
        </div>
      )}
      {filtrosFijos.map(
        ({ titulo, value, opciones, onChange }) =>
          opciones.length !== 0 && (
            <div key={titulo} className={styles.filtroItem}>
              <h3>{titulo}:</h3>
              {ordenarOpciones(opciones, value)
                .slice(0, MAX_OPCIONES_VISIBLES)
                .map(({ label, valor }) => (
                  <RadioInput
                    key={valor}
                    valor={label}
                    isSelected={value === valor}
                    onClick={(l) => {
                      setBusqueda('')
                      if (l === label) {
                        onChange(valor)
                      } else {
                        onChange('')
                      }
                    }}
                  />
                ))}
              {opciones.length > MAX_OPCIONES_VISIBLES && (
                <span
                  className={styles.verMas}
                  onClick={() => verTodas(opciones, 'filtroFijo', titulo, onChange)}
                >
                  Ver Todas
                </span>
              )}
            </div>
          )
      )}
      {mostrarFiltroPrecio && (
        <div className={styles.filtroItem}>
          <h3>Precio:</h3>
          <div className={styles.precioInputs}>
            <input
              type="number"
              placeholder="Desde"
              value={precioDesde}
              onChange={(e) => handlePrecioChange(e.target.value, 'desde')}
              onKeyDown={handleKeyDownPrecio}
              className={styles.precioInput}
              min="0"
            />
            <input
              type="number"
              placeholder="Hasta"
              value={precioHasta}
              onChange={(e) => handlePrecioChange(e.target.value, 'hasta')}
              onKeyDown={handleKeyDownPrecio}
              className={styles.precioInput}
              min="0"
            />
          </div>
        </div>
      )}
      {Object.entries(filtrosCaracteristicas).map(([etiqueta, valores]) => (
        <div className={styles.filtroItem} key={etiqueta}>
          <h3>{etiqueta}:</h3>
          {valores.slice(0, MAX_OPCIONES_VISIBLES).map((valor) => {
            const isSelected = filtrosSeleccionados[etiqueta] === valor
            return (
              <RadioInput
                key={valor}
                onClick={(v) => {
                  const nuevosFiltros = {
                    ...filtrosSeleccionados,
                    [etiqueta]: v
                  }
                  dispatch(setFiltrosSeleccionados(nuevosFiltros))
                }}
                valor={valor}
                isSelected={isSelected}
              />
            )
          })}
          {valores.length > MAX_OPCIONES_VISIBLES && (
            <span
              className={styles.verMas}
              onClick={() =>
                verTodas(
                  valores.map((v) => ({ value: v, label: v })),
                  'caracteristica',
                  etiqueta
                )
              }
            >
              Ver Todas
            </span>
          )}
        </div>
      ))}
      {fechaSeleccionada && fechaSeleccionada.length > 0 && (
        <div className="row">
          {fechaSeleccionada.map((fecha) => (
            <FechaSelect
              key={fecha.label}
              fecha={fecha.fecha}
              guardarFecha={fecha.onChange}
              label={fecha.label}
              isSelected={Boolean(fecha.fecha)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

Filtros.propTypes = {
  publicaciones: PropTypes.arrayOf(PropTypes.object),
  filtrosFijos: PropTypes.arrayOf(
    PropTypes.shape({
      titulo: PropTypes.string.isRequired,
      value: PropTypes.any,
      opciones: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string.isRequired,
          valor: PropTypes.any.isRequired
        })
      ).isRequired,
      onChange: PropTypes.func.isRequired
    })
  ).isRequired,
  setVendedor: PropTypes.func,
  vendedor: PropTypes.shape({
    id: PropTypes.number,
    label: PropTypes.string
  }),
  buscador: PropTypes.shape({
    onChange: PropTypes.func,
    placeholder: PropTypes.string
  }),
  mostrarFiltroPrecio: PropTypes.bool,
  provinciaSeleccionada: PropTypes.string,
  localidadSeleccionada: PropTypes.string,
  setProvinciaSeleccionada: PropTypes.func,
  setLocalidadSeleccionada: PropTypes.func,
  fechaSeleccionada: PropTypes.arrayOf(
    PropTypes.shape({
      fecha: PropTypes.string,
      onChange: PropTypes.func,
      label: PropTypes.string
    })
  ),
  mostrarLimpiarFiltros: PropTypes.bool,
  limpiarFiltros: PropTypes.func
}
